/**
 * OmniSearch Component
 * 
 * Global search modal (Cmd+K) for admin panel.
 * Searches across users, organisations, and invoices.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { adminApi, SearchResult } from '@/lib/admin-api';

interface OmniSearchProps {
  isOpen: boolean;
  onClose: () => void;
}

const Icons = {
  Search: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  ),
  User: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  ),
  Organisation: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  ),
  Invoice: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  Spinner: () => (
    <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  ),
};

const iconByType: Record<string, React.FC> = {
  user: Icons.User,
  organisation: Icons.Organisation,
  invoice: Icons.Invoice,
};

export default function OmniSearch({ isOpen, onClose }: OmniSearchProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setResults([]);
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Debounced search
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setIsLoading(true);
      try {
        const response = await adminApi.search(query);
        if (response.success && response.data) {
          const allResults = [
            ...response.data.users,
            ...response.data.organisations,
            ...response.data.invoices,
          ];
          setResults(allResults);
          setSelectedIndex(0);
        }
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  // Navigate to selected result
  const navigateToResult = useCallback((result: SearchResult) => {
    onClose();
    switch (result.type) {
      case 'user':
        navigate(`/admin/users/${result.id}`);
        break;
      case 'organisation':
        navigate(`/admin/orgs/${result.id}`);
        break;
      case 'invoice':
        // Invoices might need org context - for now just close
        navigate(`/admin/orgs`);
        break;
    }
  }, [navigate, onClose]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'Escape':
        onClose();
        break;
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(i => Math.min(i + 1, results.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(i => Math.max(i - 1, 0));
        break;
      case 'Enter':
        if (results[selectedIndex]) {
          navigateToResult(results[selectedIndex]);
        }
        break;
    }
  }, [results, selectedIndex, navigateToResult, onClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 dark:bg-black/70 z-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-x-4 top-[15%] mx-auto max-w-xl z-50">
        <div className="bg-surface-overlay rounded-xl shadow-2xl border border-edge overflow-hidden">
          {/* Search input */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-edge">
            <div className="text-content-quaternary">
              {isLoading ? <Icons.Spinner /> : <Icons.Search />}
            </div>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search users, organisations, invoices..."
              className="flex-1 bg-transparent text-content placeholder-content-quaternary outline-none text-base"
            />
            <kbd className="px-2 py-1 text-xs text-content-quaternary bg-surface-tertiary rounded border border-edge-strong">
              ESC
            </kbd>
          </div>

          {/* Results */}
          <div className="max-h-96 overflow-y-auto">
            {results.length === 0 && query && !isLoading && (
              <div className="px-4 py-8 text-center text-content-tertiary">
                No results found for "{query}"
              </div>
            )}

            {results.length === 0 && !query && (
              <div className="px-4 py-8 text-center text-content-tertiary">
                <p className="mb-2">Start typing to search...</p>
                <p className="text-sm">
                  Search users by email or name, organisations by name, or invoices by number
                </p>
              </div>
            )}

            {results.length > 0 && (
              <div className="py-2">
                {results.map((result, index) => {
                  const Icon = iconByType[result.type] || Icons.Search;
                  return (
                    <button
                      key={`${result.type}-${result.id}`}
                      onClick={() => navigateToResult(result)}
                      className={cn(
                        'flex items-center gap-3 w-full px-4 py-2.5 text-left transition-colors',
                        index === selectedIndex
                          ? 'bg-amber-50 dark:bg-amber-900/30'
                          : 'hover:bg-surface-hover'
                      )}
                    >
                      <div className={cn(
                        'w-8 h-8 rounded-lg flex items-center justify-center',
                        result.type === 'user' && 'border border-qual-1/40 bg-qual-1/20 text-qual-1',
                        result.type === 'organisation' && 'border border-qual-2/40 bg-qual-2/20 text-qual-2',
                        result.type === 'invoice' && 'border border-qual-3/40 bg-qual-3/20 text-qual-3'
                      )}>
                        <Icon />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-content truncate">
                          {result.label}
                        </p>
                        <p className="text-xs text-content-tertiary truncate">
                          {result.sublabel}
                        </p>
                      </div>
                      <span className={cn(
                        'px-2 py-0.5 text-xs rounded-full',
                        result.type === 'user' && 'border border-qual-1/40 bg-qual-1/20 text-qual-1',
                        result.type === 'organisation' && 'border border-qual-2/40 bg-qual-2/20 text-qual-2',
                        result.type === 'invoice' && 'border border-qual-3/40 bg-qual-3/20 text-qual-3'
                      )}>
                        {result.type}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 border-t border-edge bg-surface-secondary">
            <div className="flex items-center gap-4 text-xs text-content-tertiary">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-surface rounded border border-edge-strong">↑</kbd>
                <kbd className="px-1.5 py-0.5 bg-surface rounded border border-edge-strong">↓</kbd>
                Navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-surface rounded border border-edge-strong">↵</kbd>
                Select
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-surface rounded border border-edge-strong">ESC</kbd>
                Close
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
